import PropTypes from 'prop-types';

import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Divider from '@zenbusiness/zui/Divider';
import Grid from '@zenbusiness/zui/Grid';
import Text from '@zenbusiness/zui/Text';

import ChargesRow from './ChargesRow';

import * as Styled from '../OrderView.styles';

const LOCALIZED_TEXT = {
  heading: 'orderView.charges.heading',
  description: 'orderView.charges.description',
  orderDate: 'orderView.charges.orderDate',
  paymentStatus: 'orderView.charges.paymentStatus',
  amount: 'orderView.charges.amount',
  tableAriaLabel: 'orderView.charges.tableAriaLabel',
  stockFeeBreakdown: 'orderView.charges.stockFeeBreakdown',
  authorizedShares: 'orderView.charges.authorizedShares',
  parValue: 'orderView.charges.parValue',
  stateProcessingFee: 'orderView.charges.stateProcessingFee',
  pageFee: 'orderView.charges.pageFee',
  stockTotal: 'orderView.charges.stockTotal',
};

const ChargesTable = ({
  loading,
  orderTasks,
  orderTransaction,
  stockSetupData = {},
  isPlan,
  primaryOrderTask,
  transactions,
}) => {
  const { formatMessage: fm } = useIntl();

  const TABLE_HEADER = [
    { title: fm({ id: LOCALIZED_TEXT.description }), align: 'left' },
    { title: fm({ id: LOCALIZED_TEXT.orderDate }), align: 'left' },
    { title: fm({ id: LOCALIZED_TEXT.paymentStatus }), align: 'left' },
    { title: fm({ id: LOCALIZED_TEXT.amount }), align: 'left' },
  ];

  const {
    parValue: value,
    shareQty: shares,
    stockTotal: total,
    fees: {
      expediteFee: { feeAmount: expediteFeeAmount = 0 } = {},
      stateDocumentFee,
      stateProcessingFee: { feeAmount: stateProcessingFeeAmount = 0 } = {},
    } = {},
  } = stockSetupData;

  const stateProcessingFees = (Number(expediteFeeAmount) + Number(stateProcessingFeeAmount)).toFixed(2);

  let formationTask = null;
  let formationTransaction = {
    amount: null,
    orderItemUuid: null,
    transactionDatetime: null,
    description: null,
  };
  let items = transactions?.filter(({ amount }) => Number(amount) !== 0);
  if (isPlan) {
    formationTask = orderTasks?.find(({ orderTaskType }) => orderTaskType.includes('formation'));
    if (formationTask) {
      formationTransaction =
        transactions.find(({ orderItemUuid }) => orderItemUuid === formationTask.orderItemUuid) || formationTransaction;
    }

    const coupons = transactions.filter(({ description }) => description?.includes('_off_'));
    items = transactions.filter(({ description }) => !description?.includes('_off_')).map((item) => ({ ...item }));
    if (formationTask) {
      items = items.filter(({ orderItemUuid }) => orderItemUuid !== formationTask.orderItemUuid);
    }

    coupons.forEach((coupon) => {
      const item = items.find(
        ({ orderItemUuid, transactionDatetime }) =>
          orderItemUuid === coupon.orderItemUuid && transactionDatetime === coupon.transactionDatetime,
      );
      if (item) {
        item.amount = (Number(item.amount) + Number(coupon.amount)).toFixed(2).toString();
      }
    });
  }

  const hasItems = items.length > 0;
  const hasStockSetupData = Object.keys(stockSetupData).length > 0;
  if (!hasItems && !hasStockSetupData && (!isPlan || !formationTask)) {
    return null;
  }

  return (
    <Styled.ChargesTableRoot>
      <Grid container direction="column" wrap="nowrap">
        <Grid item>
          <Text variant="body1" fontWeight="bold">
            <FormattedMessage id={LOCALIZED_TEXT.heading} />
          </Text>
        </Grid>
        <Grid item>
          <Divider />
        </Grid>
        <Grid item>
          <Styled.StyledTable aria-label={fm({ id: LOCALIZED_TEXT.tableAriaLabel })}>
            <thead>
              <tr>
                {TABLE_HEADER.map(({ title, align }) => (
                  <th align={align} key={title}>
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <ChargesRow
                  isDiscount={item.type !== 'refund' && Number(item.amount) < 0}
                  isRefund={item.type === 'refund'}
                  key={`${item.orderItemUuid}-${item.type}-${item.transactionDatetime}`}
                  loading={loading}
                  orderTasks={orderTasks}
                  parentOrderTask={primaryOrderTask}
                  parentTransaction={orderTransaction}
                  transaction={item}
                />
              ))}
              {isPlan && (
                <ChargesRow
                  key="formation-row"
                  loading={loading}
                  orderTasks={orderTasks}
                  transaction={formationTransaction}
                />
              )}
            </tbody>
          </Styled.StyledTable>
        </Grid>
      </Grid>
      {Object.keys(stockSetupData).length > 0 && (
        <Grid container direction="column" sx={{ paddingTop: '1.875rem' }} wrap="nowrap">
          <Grid item>
            <Text variant="body1" fontWeight="bold">
              <FormattedMessage id={LOCALIZED_TEXT.stockFeeBreakdown} />
            </Text>
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
          <Styled.OrderRow>
            <Text variant="body2">
              <FormattedMessage id={LOCALIZED_TEXT.authorizedShares} />
            </Text>
            <Text variant="body2">{shares}</Text>
          </Styled.OrderRow>
          <Styled.OrderRow>
            <Text variant="body2">
              <FormattedMessage id={LOCALIZED_TEXT.parValue} />
            </Text>
            <Text variant="body2">{`$${(Number(value) || 0).toFixed(5)}`}</Text>
          </Styled.OrderRow>
          <Grid item>
            <Divider />
          </Grid>
          <Styled.OrderRow>
            <Text variant="body2">
              <FormattedMessage id={LOCALIZED_TEXT.stateProcessingFee} />
            </Text>
            <Text variant="body2">{`$${stateProcessingFees}`}</Text>
          </Styled.OrderRow>
          <Styled.OrderRow>
            <Text variant="body2">
              <FormattedMessage id={LOCALIZED_TEXT.pageFee} />
            </Text>
            <Text variant="body2">{`$${(Number(stateDocumentFee?.feeAmount) || 0).toFixed(2)}`}</Text>
          </Styled.OrderRow>
          <Styled.OrderRow>
            <Text variant="body2">
              <FormattedMessage id={LOCALIZED_TEXT.stockTotal} />
            </Text>
            <Text variant="body2">{`$${(Number(total) || 0).toFixed(2)}`}</Text>
          </Styled.OrderRow>
        </Grid>
      )}
    </Styled.ChargesTableRoot>
  );
};

ChargesTable.propTypes = {
  loading: PropTypes.bool,
  isPlan: PropTypes.bool.isRequired,
  transactions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  orderTasks: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  orderTransaction: PropTypes.shape({}),
  stockSetupData: PropTypes.shape({
    parValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shareQty: PropTypes.number,
    fees: PropTypes.shape({}),
  }),
  primaryOrderTask: PropTypes.shape({}),
};

export default ChargesTable;
